import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';
import './styles.css';
import '../blog.css';
import './NewsGrid.css';
import '../journal.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AdIcons } from '../components/Ad';
import { Link, useSearchParams } from 'react-router-dom';

const DB_BACKED_TYPES = new Set();
const FEATURED_LAYOUT_TYPES = ['blog', 'journal', 'sermon'];
const SIDEBAR_TYPES = new Set(['journal', 'blog', 'sermon']);
const SIDEBAR_LIMIT = 8;
const LIMITED_LIST_TYPES = new Set(['journal']);
const MAX_LIST_ITEMS = 6;
const JOURNAL_FETCH_PAGE_SIZE = 200;
const LATEST_SECTION_LIMIT = 6;
const CATEGORY_ORDER = ['society', 'politics', 'culture', 'tech', 'science', 'bible', 'theology', 'philosophy', 'cult', 'opinion', 'finance', 'family', 'life'];
const CATEGORY_LABELS = {
  tech: 'Tech',
  science: 'Science',
  society: 'Society',
  politics: 'Politics',
  culture: 'Culture',
  bible: 'Bible',
  theology: 'Theology',
  philosophy: 'Philosophy',
  cult: 'Cult',
  opinion: 'Opinion',
  finance: 'Finance',
  family: 'Family',
  life: 'Life'
};
const CATEGORY_NAV_ITEMS = ['all', ...CATEGORY_ORDER];
const CATEGORY_DISPLAY_LABELS = {
  all: 'ALL',
  tech: 'TECH',
  science: 'SCIENCE',
  society: 'SOCIETY',
  politics: 'POLITICS',
  culture: 'CULTURE',
  bible: 'BIBLE',
  theology: 'THEOLOGY',
  philosophy: 'PHILOSOPHY',
  cult: 'CULT',
  opinion: 'OPINION',
  finance: 'FINANCE',
  family: 'FAMILY',
  life: 'LIFE'
};
const FALLBACK_IMAGE = '/static/images/i4.jpg';

function ListComponent({ type, title }) {
  const [articles, setArticles] = useState([]);
  const [articlesWithMeta, setArticlesWithMeta] = useState([]);
  const [allArticlesWithMeta, setAllArticlesWithMeta] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [useDatabase, setUseDatabase] = useState(DB_BACKED_TYPES.has(type));
  const [sidebarArticles, setSidebarArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const FEATURED_COUNT = 1;
  const GRID_COLUMNS = 3;
  const GRID_ROWS_FOR_FULL_GRID = 4; // Ensures the grid below the featured item has 4 complete rows
  const isJournal = type === 'journal';
  const hasSidebar = SIDEBAR_TYPES.has(type);
  const shouldLimitToSix = LIMITED_LIST_TYPES.has(type);

  const [searchParams] = useSearchParams();
  const activeCategoryParam = searchParams.get('category');
  const activeCategory = activeCategoryParam ? activeCategoryParam.toLowerCase() : null;
  const isCategoryFilterActive = isJournal && activeCategory && activeCategory !== 'all';
  const shouldLimit = !isCategoryFilterActive && shouldLimitToSix && !searchQuery.trim();
  const useJournalLayout = isJournal && !isCategoryFilterActive && !searchQuery.trim();
  const postsPerPage = shouldLimit
    ? MAX_LIST_ITEMS
    : FEATURED_LAYOUT_TYPES.includes(type)
      ? FEATURED_COUNT + GRID_COLUMNS * GRID_ROWS_FOR_FULL_GRID
      : 12;

  useEffect(() => {
    setUseDatabase(DB_BACKED_TYPES.has(type));
    setCurrentPage(1);
  }, [type]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  useEffect(() => {
    if (!hasSidebar) {
      setSidebarArticles([]);
    }
  }, [hasSidebar]);

  useEffect(() => {
    if (!useDatabase) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    setArticles([]);

    const fetchArticles = async () => {
      try {
        const effectivePageSize = (shouldLimit && type === 'journal') || (type === 'journal' && isCategoryFilterActive)
          ? JOURNAL_FETCH_PAGE_SIZE
          : postsPerPage;
        const response = await fetch(`/api/articles?type=${type}&page=${currentPage}&pageSize=${effectivePageSize}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to fetch articles from API');
        }
        const data = await response.json();
        if (!isMounted) {
          return;
        }
        const normalizedData = data.data || [];
        const trimmedData = shouldLimit
          ? normalizedData.slice(0, MAX_LIST_ITEMS)
          : normalizedData;

        setAllArticlesWithMeta(normalizedData);
        setArticlesWithMeta(trimmedData);
        setTotalPages(shouldLimit ? 1 : (data.pagination?.totalPages || 1));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          if (isMounted) {
            setUseDatabase(false);
            setCurrentPage(1);
          }
        }
      }
    };

    fetchArticles();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [type, currentPage, postsPerPage, useDatabase, shouldLimit, isCategoryFilterActive]);

  useEffect(() => {
    if (!useDatabase || !hasSidebar) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchSidebar = async () => {
      try {
        const response = await fetch(`/api/articles?type=${type}&page=1&pageSize=${SIDEBAR_LIMIT}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to fetch sidebar articles from API');
        }
        const data = await response.json();
        if (isMounted) {
          const normalizedData = data.data || [];
          const trimmedSidebar = shouldLimit
            ? normalizedData.slice(0, MAX_LIST_ITEMS)
            : normalizedData;
          setSidebarArticles(trimmedSidebar);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      }
    };

    fetchSidebar();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [type, useDatabase, hasSidebar, shouldLimit]);

  useEffect(() => {
    if (useDatabase) {
      return;
    }

    fetch(`/static/markdown/${type}/list.json`)
      .then((response) => response.json())
      .then((data) => {
        const list = Array.isArray(data.articles) ? data.articles : [];
        setArticles(list);
      })
      .catch((error) => {
        console.error('Error fetching list:', error);
      });

  }, [type, useDatabase]);

  // Fetch metadata for each article
  useEffect(() => {
    if (useDatabase || articles.length === 0) return;

    const fetchMetadata = async () => {
      const reversedArticles = articles.slice().reverse();
      const metaPromises = reversedArticles.map(async (article) => {
        try {
          const response = await fetch(`/static/markdown/${type}/${article.id}.md`);
          const text = await response.text();

          // Extract title (first H1)
          const titleMatch = text.match(/^#\s+(.+)$/m);
          const extractedTitle = titleMatch ? titleMatch[1] : 'タイトルなし';

          // Extract first image
          const imageMatch = text.match(/!\[.*?\]\((.*?)\)/);
          const image = imageMatch && imageMatch[1]
            ? imageMatch[1]
            : '/static/images/i4.jpg';

          // Extract description (first paragraph after title)
          const lines = text.split('\n');
          let description = '';
          let foundTitle = false;
          for (let line of lines) {
            if (line.startsWith('#')) {
              foundTitle = true;
              continue;
            }
            if (foundTitle && line.trim() && !line.startsWith('!') && !line.startsWith('[') && !line.startsWith('*')) {
              description = line.replace(/[*_`~[\]()]/g, '').substring(0, 100);
              break;
            }
          }

          return {
            ...article,
            title: extractedTitle,
            image,
            description
          };
        } catch (error) {
          console.error(`Error fetching metadata for ${article.id}:`, error);
          return {
            ...article,
            title: 'タイトルなし',
            image: '/static/images/i4.jpg',
            description: ''
          };
        }
      });

      const metaData = await Promise.all(metaPromises);
      const trimmedMetaData = shouldLimit
        ? metaData.slice(0, MAX_LIST_ITEMS)
        : metaData;

      setAllArticlesWithMeta(metaData);
      setArticlesWithMeta(trimmedMetaData);
      setTotalPages(shouldLimit ? 1 : Math.max(Math.ceil(trimmedMetaData.length / postsPerPage), 1));
      if (hasSidebar) {
        const sidebarSlice = shouldLimit
          ? trimmedMetaData
          : trimmedMetaData.slice(0, SIDEBAR_LIMIT);
        setSidebarArticles(sidebarSlice);
      }
    };

    fetchMetadata();
  }, [articles, type, useDatabase, hasSidebar, postsPerPage, shouldLimit]);

  // Calculate the current articles to display
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const paginatedArticles = useDatabase
    ? articlesWithMeta
    : articlesWithMeta.slice(indexOfFirstPost, indexOfLastPost);

  const filteredArticles = useMemo(() => {
    if (!isCategoryFilterActive) {
      return [];
    }

    return allArticlesWithMeta.filter((article) => {
      if (!article) {
        return false;
      }
      const rawCategory = article.category;
      if (!rawCategory) {
        return false;
      }
      return String(rawCategory).toLowerCase() === activeCategory;
    });
  }, [allArticlesWithMeta, isCategoryFilterActive, activeCategory]);

  const searchFilteredArticles = useMemo(() => {
    if (!searchQuery.trim()) {
      return isCategoryFilterActive ? filteredArticles : paginatedArticles;
    }

    const query = searchQuery.toLowerCase();
    const articlesToSearch = isCategoryFilterActive ? filteredArticles : allArticlesWithMeta;

    return articlesToSearch.filter((article) => {
      if (!article) {
        return false;
      }
      const title = article.title ? String(article.title).toLowerCase() : '';
      const description = article.description ? String(article.description).toLowerCase() : '';
      return title.includes(query) || description.includes(query);
    });
  }, [searchQuery, filteredArticles, isCategoryFilterActive, allArticlesWithMeta, paginatedArticles]);

  const baseArticles = isCategoryFilterActive ? filteredArticles : paginatedArticles;
  const currentArticles = searchQuery.trim() ? searchFilteredArticles : baseArticles;

  const effectiveTotalPages = useMemo(() => {
    if (shouldLimit) {
      return 1;
    }
    return Math.max(totalPages || 1, 1);
  }, [shouldLimit, totalPages]);

  const paginationRange = useMemo(() => {
    if (effectiveTotalPages <= 1) {
      return [1];
    }

    if (effectiveTotalPages <= 7) {
      return Array.from({ length: effectiveTotalPages }, (_, index) => index + 1);
    }

    const range = [];
    const siblingCount = 1;
    let leftSibling = Math.max(currentPage - siblingCount, 2);
    let rightSibling = Math.min(currentPage + siblingCount, effectiveTotalPages - 1);

    if (currentPage <= 2) {
      leftSibling = 2;
      rightSibling = Math.min(4, effectiveTotalPages - 1);
    } else if (currentPage >= effectiveTotalPages - 1) {
      leftSibling = Math.max(effectiveTotalPages - 3, 2);
      rightSibling = effectiveTotalPages - 1;
    }

    range.push(1);

    if (leftSibling > 2) {
      range.push('left-ellipsis');
    }

    for (let page = leftSibling; page <= rightSibling; page += 1) {
      range.push(page);
    }

    if (rightSibling < effectiveTotalPages - 1) {
      range.push('right-ellipsis');
    }

    range.push(effectiveTotalPages);

    return range;
  }, [effectiveTotalPages, currentPage]);

  useEffect(() => {
    if (!hasSidebar) {
      return;
    }
    if (isCategoryFilterActive) {
      setSidebarArticles(filteredArticles.slice(0, SIDEBAR_LIMIT));
    }
  }, [filteredArticles, hasSidebar, isCategoryFilterActive]);

  const goToPage = (page) => {
    if (page < 1 || page > effectiveTotalPages || page === currentPage) {
      return;
    }
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextPage = () => {
    if (currentPage < effectiveTotalPages) {
      goToPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const showPagination = !shouldLimit
    && !isCategoryFilterActive
    && !searchQuery.trim()
    && effectiveTotalPages > 1;

  const showCategoryListView = isJournal && isCategoryFilterActive;

  const categoryListView = showCategoryListView ? (
    currentArticles.length > 0 ? (
      <div className="journal-category-list">
        {currentArticles.map((article) => (
          <a
            key={article.id}
            href={`/${type}/${article.id}`}
            className="journal-category-list-item"
          >
            <div
              className="journal-category-list-thumb"
              style={{ backgroundImage: `url(${article.image || FALLBACK_IMAGE})` }}
            />
            <div className="journal-category-list-content">
              {article.creationDate && (
                <span className="journal-category-list-date">{article.creationDate}</span>
              )}
              <h3 className="journal-category-list-title">{article.title || 'タイトルなし'}</h3>
              {article.description && (
                <p className="journal-category-list-description">{article.description}</p>
              )}
            </div>
          </a>
        ))}
      </div>
    ) : (
      <div className="journal-category-list-empty">
        <p>このカテゴリの記事はまだありません。</p>
      </div>
    )
  ) : null;

  // Set OG tags based on type
  const metaTitle = `${title} - ASMRキリスト教会`;
  const metaDescriptionMap = {
    blog: 'ASMRキリスト教会のブログ記事一覧。聖書、信仰、日常について綴っています。',
    journal: 'ASMRキリスト教会の聖書新聞一覧。最新のお知らせやニュースをまとめています。'
  };
  const metaDescription = metaDescriptionMap[type] || `${title}の一覧ページです。`;
  const metaImage = ['blog', 'journal'].includes(type)
    ? 'https://www.asmrchurch.com/static/images/c5.jpg'
    : 'https://www.asmrchurch.com/static/images/i4.jpg';
  const metaUrl = `https://www.asmrchurch.com/${type}`;

  const categorySectionsData = useMemo(() => {
    if (!useJournalLayout || allArticlesWithMeta.length === 0) {
      return [];
    }

    const categoryMap = new Map();

    allArticlesWithMeta.forEach((article) => {
      if (!article) {
        return;
      }
      const rawCategory = article.category;
      if (!rawCategory) {
        return;
      }
      const normalizedCategory = String(rawCategory).toLowerCase();
      if (!categoryMap.has(normalizedCategory)) {
        categoryMap.set(normalizedCategory, []);
      }
      categoryMap.get(normalizedCategory).push(article);
    });

    if (categoryMap.size === 0) {
      return [];
    }

    const uniqueCategories = Array.from(categoryMap.keys());
    const orderedCategories = [
      ...CATEGORY_ORDER.filter((category) => categoryMap.has(category)),
      ...uniqueCategories.filter((category) => !CATEGORY_ORDER.includes(category))
    ];

    return orderedCategories.map((category) => ({
      category,
      label: CATEGORY_LABELS[category] || category.charAt(0).toUpperCase() + category.slice(1),
      articles: categoryMap.get(category) || []
    }));
  }, [allArticlesWithMeta, useJournalLayout]);

  const categorySections = useJournalLayout && categorySectionsData.length > 0 ? (
    <div className="journal-category-sections">
      {categorySectionsData.map(({ category, label, articles }) => (
        <section key={category} className="journal-category-section">
          <div className="journal-category-header">
            <span className="journal-category-label">{label}</span>
            {articles.length > 0 && (
              <Link
                to={`/journal?category=${category}`}
                className="journal-category-more"
              >
                もっと見る
              </Link>
            )}
          </div>
          <div className="journal-category-scroll">
            {articles.map((article) => (
              <a
                key={`${category}-${article.id}`}
                href={`/${type}/${article.id}`}
                className="journal-category-card"
              >
                <div
                  className="journal-category-card-image"
                  style={{ backgroundImage: `url(${article.image || FALLBACK_IMAGE})` }}
                />
                <div className="journal-category-card-body">
                  {article.creationDate && (
                    <span className="journal-category-card-date">{article.creationDate}</span>
                  )}
                  <h3 className="journal-category-card-title">{article.title || 'タイトルなし'}</h3>
                  {article.description && (
                    <p className="journal-category-card-description">{article.description}</p>
                  )}
                </div>
              </a>
            ))}
          </div>
        </section>
      ))}
    </div>
  ) : null;

  const featuredArticleId = currentArticles.length > 0 ? currentArticles[0].id : null;

  const featuredArticle = currentArticles.length > 0 ? (
    <a
      href={`/${type}/${currentArticles[0].id}`}
      className="featured-article"
    >
      <div
        className="featured-image"
        style={{ backgroundImage: `url(${currentArticles[0].image})` }}
      >
        <div className="featured-overlay">
          <span className="featured-date">{currentArticles[0].creationDate}</span>
          <h2 className="featured-title">{currentArticles[0].title}</h2>
          <p className="featured-description">{currentArticles[0].description}</p>
        </div>
      </div>
    </a>
  ) : null;

  const latestArticles = useMemo(() => {
    if (!useJournalLayout || allArticlesWithMeta.length === 0) {
      return [];
    }

    return allArticlesWithMeta
      .filter((article) => article && article.id !== featuredArticleId)
      .slice(0, LATEST_SECTION_LIMIT);
  }, [useJournalLayout, allArticlesWithMeta, featuredArticleId]);

  const latestSection = useJournalLayout && latestArticles.length > 0 ? (
    <section className="journal-category-section">
      <div className="journal-category-header">
        <span className="journal-category-label">Latest</span>
        <Link to="/journal" className="journal-category-more">
          もっと見る
        </Link>
      </div>
      <div className="journal-category-scroll">
        {latestArticles.map((article) => (
          <a
            key={`latest-${article.id}`}
            href={`/${type}/${article.id}`}
            className="journal-category-card"
          >
            <div
              className="journal-category-card-image"
              style={{ backgroundImage: `url(${article.image || FALLBACK_IMAGE})` }}
            />
            <div className="journal-category-card-body">
              {article.creationDate && (
                <span className="journal-category-card-date">{article.creationDate}</span>
              )}
              <h3 className="journal-category-card-title">{article.title || 'タイトルなし'}</h3>
              {article.description && (
                <p className="journal-category-card-description">{article.description}</p>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  ) : null;

  const newsGrid = (!useJournalLayout) && currentArticles.slice(1).length > 0 ? (
    <div className="news-grid">
      {currentArticles.slice(1).map((article) => (
        <a
          key={article.id}
          href={`/${type}/${article.id}`}
          className="news-card"
        >
          <div
            className="news-card-image"
            style={{ backgroundImage: `url(${article.image})` }}
          />
          <div className="news-card-content">
            <span className="news-card-date">{article.creationDate}</span>
            <h3 className="news-card-title">{article.title}</h3>
            <p className="news-card-description">{article.description}</p>
          </div>
        </a>
      ))}
    </div>
  ) : null;

  return (
    <div className={isJournal ? 'journal-page journal-list-page' : ''}>
      <Header
        title={metaTitle}
        description={metaDescription}
        url={metaUrl}
        image={metaImage}
      />
      <div className={`news-container ${isJournal ? 'journal-theme' : ''}`}>
        <div className="news-header-container">
          <div>
          <h1 className="news-title">{title}</h1>
          {isJournal && (
            <span className="journal-subtitle">ASMRキリスト教会の機関紙</span>
          )}
          </div>
          <div className="news-search-box">
            <input
              type="text"
              placeholder="記事を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="news-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="news-search-clear"
                aria-label="検索をクリア"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {isJournal && (
          <nav className="journal-category-nav">
            {CATEGORY_NAV_ITEMS.map((categoryKey) => {
              const label = CATEGORY_DISPLAY_LABELS[categoryKey] || categoryKey.toUpperCase();
              const isActiveLink = categoryKey === 'all'
                ? !isCategoryFilterActive
                : activeCategory === categoryKey;
              const to = categoryKey === 'all'
                ? '/journal'
                : `/journal?category=${categoryKey}`;
              return (
                <Link
                  key={categoryKey}
                  to={to}
                  className={`journal-category-nav-link${isActiveLink ? ' active' : ''}`}
                >
                  {label}
                </Link>
              );
            })}
          </nav>
        )}

        {searchQuery.trim() && currentArticles.length === 0 ? (
          <div className="news-search-no-results">
            <p>「{searchQuery}」に一致する記事が見つかりませんでした。</p>
          </div>
        ) : null}

        {hasSidebar ? (
          <>
            <div className={`news-sidebar-layout ${isJournal ? 'journal-list-layout' : ''}`}>
              <div className={`news-sidebar-main ${isJournal ? 'journal-list-main' : ''}`}>
                {useJournalLayout ? (
                  featuredArticle
                ) : showCategoryListView ? (
                  categoryListView
                ) : (
                  <>
                    {featuredArticle}
                    {newsGrid}
                  </>
                )}
              </div>
              {sidebarArticles.length > 0 && (
                <aside className={isJournal ? 'journal-sidebar' : 'news-sidebar'}>
                  <h2 className={isJournal ? 'journal-sidebar-title' : 'news-sidebar-title'}>最新記事</h2>
                  <ul className={isJournal ? 'journal-sidebar-list' : 'news-sidebar-list'}>
                    {sidebarArticles.map((article) => (
                      <li
                        key={article.id}
                        className={isJournal ? 'journal-sidebar-item' : 'news-sidebar-item'}
                      >
                        <a
                          href={`/${type}/${article.id}`}
                          className={isJournal ? 'journal-sidebar-link' : 'news-sidebar-link'}
                        >
                          {article.title}
                        </a>
                        {article.creationDate && (
                          <span
                            className={isJournal ? 'journal-sidebar-date' : 'news-sidebar-date'}
                          >
                            {article.creationDate}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </aside>
              )}
            </div>

            {useJournalLayout && (latestSection || categorySections) && (
              <div className="journal-full-width">
                {latestSection}
                {categorySections}
              </div>
            )}
          </>
        ) : (
          <>
            {featuredArticle}
            {useJournalLayout ? (
              <div className="journal-full-width">
                {latestSection}
                {categorySections}
              </div>
            ) : (
              newsGrid
            )}
          </>
        )}

        {shouldLimit && (
          <div className="ads-inline-section">
            {isJournal && (
              <>
                <div className="kirishin-link">
                  <a
                    href="https://www.kirishin.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/static/images/kirisin.svg`}
                      alt="キリスト新聞"
                    />
                  </a>
                </div>
                <div className="cloudchurch-link">
                  <a
                    href="https://www.cloudchurch-japan.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/static/images/cloudchurch.png`}
                      alt="Cloud Church"
                    />
                  </a>
                </div>
                <div className="reader-submission-notice">
                  <p>読者投稿はkei@sugano.worksまで</p>
                </div>
              </>
            )}
            <AdIcons />
          </div>
        )}

        {/* Pagination controls */}
        {showPagination && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="pagination-btn pagination-btn--ghost"
              aria-label="最初のページへ"
            >
              最初へ
            </button>
            <button
              type="button"
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-btn"
              aria-label="前のページへ"
            >
              ← 前へ
            </button>
            <div className="pagination-pages" role="navigation" aria-label="ページ一覧">
              {paginationRange.map((item) => (
                typeof item === 'number' ? (
                  <button
                    key={`page-${item}`}
                    type="button"
                    onClick={() => goToPage(item)}
                    className={`pagination-number${currentPage === item ? ' is-active' : ''}`}
                    aria-current={currentPage === item ? 'page' : undefined}
                    disabled={currentPage === item}
                  >
                    {item}
                  </button>
                ) : (
                  <span key={item} className="pagination-ellipsis" aria-hidden="true">…</span>
                )
              ))}
            </div>
            <button
              type="button"
              onClick={nextPage}
              disabled={currentPage === effectiveTotalPages}
              className="pagination-btn"
              aria-label="次のページへ"
            >
              次へ →
            </button>
            <button
              type="button"
              onClick={() => goToPage(effectiveTotalPages)}
              disabled={currentPage === effectiveTotalPages}
              className="pagination-btn pagination-btn--ghost"
              aria-label="最後のページへ"
            >
              最後へ
            </button>
            <span className="pagecount">ページ {currentPage} / {effectiveTotalPages}</span>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default ListComponent;
